import { Camera } from 'three';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import { SingleFrameCalibrationData } from '../../../team-apis/otterdam/calibration-data';
import { dividePoints, scalePoint } from '../../utils/point-utils';

export class ProjectionCamera extends Camera {
  isPerspectiveCamera = true;
  near = 0.2;
  far = 10000;

  updateProjectionMatrix(
    focalLength: Point2D,
    principlePoint: Point2D,
    imageSize: Point2D,
  ) {
    // Code copied from ThreeJsS Matrix4.makePerspective()
    // https://threejs.org/docs/#api/en/math/Matrix4
    // Implement ourselves to have 2 separate focal lengths

    const te = this.projectionMatrix.elements;
    te.fill(0);

    // y is now 2 * focalLength_y / height
    const x = (2 * focalLength[0]) / imageSize[0];
    const y = (2 * focalLength[1]) / imageSize[1];

    // a,b need to be in the range of [-1,-1] to [1,1]
    const [a, b] = dividePoints(scalePoint(principlePoint, 2), imageSize);

    const c = -this.far / (this.far - this.near);
    const d = (-this.far * this.near) / (this.far - this.near);

    te[0] = x;
    te[5] = y;

    te[8] = 1 - a;
    te[9] = b - 1;

    te[10] = c;
    te[14] = d;

    te[11] = -1;

    this.projectionMatrixInverse.copy(this.projectionMatrix).invert();
  }
}

export function updateProjectionCamera(
  camera: ProjectionCamera,
  calibrationData: SingleFrameCalibrationData,
  imageSize: Point2D,
) {
  const { position, rotation, focalLength, principlePoint } = calibrationData;

  camera.position.fromArray(position);
  camera.quaternion.fromArray(rotation);

  camera.updateProjectionMatrix(focalLength, principlePoint, imageSize);
  camera.updateMatrixWorld(true);
}
